import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, RefreshCw, Trash2, CheckCircle2, XCircle, Clock,
  Users, Activity, ChevronDown, ChevronUp, Facebook, ExternalLink, MessageSquare,
} from 'lucide-react';
import { apiService } from '../../services/api';
import { authService } from '../../services/auth';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FanpagePlatform {
  id: string;
  page_id: string;
  page_name: string;
  page_category: string;
  picture_url: string;
  fan_count: number;
  is_active: boolean;
  webhook_verified: boolean;
  worker_uuid: string;
  last_sync_at: string;
  created_at: string;
}

interface FanpageActivity {
  id: string;
  activity_type: string;
  target_id: string;
  content: string;
  success: boolean;
  error_msg: string;
  created_at: string;
}

interface FanpagePost {
  post_id: string;
  message?: string;
  permalink_url?: string;
  created_time?: string;
  created_at?: string;
  like_count?: number;
  comment_count?: number;
  share_count?: number;
  participants?: FanpagePostParticipant[];
}

interface FanpagePostParticipant {
  participant_id?: string;
  participant_name?: string;
  profile_url?: string;
  comment_count?: number;
  last_comment_at?: string;
}

interface FanpageComment {
  comment_id: string;
  post_id?: string;
  from_id?: string;
  from_name?: string;
  profile_url?: string;
  message?: string;
  like_count?: number;
  reply_count?: number;
  is_hidden?: boolean;
  created_time?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function formatDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
}

function formatFanCount(n: number) {
  if (!n) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatMetric(n?: number) {
  if (n == null || n <= 0) return '—';
  return String(n);
}

const ACTIVITY_LABELS: Record<string, string> = {
  comment_replied: 'Trả lời bình luận',
  comment_reply: 'Trả lời bình luận',
  comment_deleted: 'Xoá bình luận',
  comment_hidden: 'Ẩn bình luận',
  comment_unhidden: 'Bỏ ẩn bình luận',
  comment_posted: 'Đăng bình luận',
  comment_liked: 'Like bình luận',
  dm_replied: 'Trả lời tin nhắn',
  post_published: 'Đăng bài',
  post_scheduled: 'Lên lịch bài đăng',
  dm_sent: 'Gửi tin nhắn',
};

// ─────────────────────────────────────────────────────────────────────────────
// Activity log sub-panel
// ─────────────────────────────────────────────────────────────────────────────
const ActivityLog: React.FC<{ fanpageId: string }> = ({ fanpageId }) => {
  const { t } = useTranslation('fanpage');
  const [activities, setActivities] = useState<FanpageActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 10;

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const nextOffset = reset ? 0 : offset;
      const resp = await apiService.get<{ activities: FanpageActivity[] }>(
        `/api/v1/fanpages/${fanpageId}/activities?limit=${LIMIT}&offset=${nextOffset}`
      );
      const activities = resp.activities ?? [];
      if (reset) {
        setActivities(activities);
        setOffset(LIMIT);
      } else {
        setActivities((prev) => [...prev, ...activities]);
        setOffset((o) => o + LIMIT);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [fanpageId, offset]);

  useEffect(() => { load(true); }, [fanpageId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && activities.length === 0) {
    return <p className="text-xs text-gray-400 py-3 text-center">{t('loadingHistory')}</p>;
  }

  if (!loading && activities.length === 0) {
    return <p className="text-xs text-gray-400 py-3 text-center">{t('noActivities')}</p>;
  }

  return (
    <div className="mt-3 space-y-1.5">
      {activities.map((a) => (
        <div key={a.id} className="flex items-start gap-2 text-[12px]">
          {a.success
            ? <CheckCircle2 size={13} className="text-green-500 flex-shrink-0 mt-0.5" />
            : <XCircle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-700">
              {t(`activityTypes.${a.activity_type}`, { defaultValue: a.activity_type })}
            </span>
            {a.content && (
              <span className="text-gray-400 ml-1 truncate">— {a.content}</span>
            )}
            {!a.success && a.error_msg && (
              <p className="text-red-400 text-[11px] mt-0.5">{a.error_msg}</p>
            )}
          </div>
          <span className="text-gray-300 flex-shrink-0">{formatDate(a.created_at)}</span>
        </div>
      ))}
      {activities.length >= offset && (
        <button
          type="button"
          onClick={() => load(false)}
          disabled={loading}
          className="text-[12px] text-blue-500 hover:underline disabled:opacity-50"
        >
          {loading ? t('loadingMore') : t('loadMore')}
        </button>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Fanpage card
// ─────────────────────────────────────────────────────────────────────────────
const FanpageCard: React.FC<{
  fp: FanpagePlatform;
  onDisconnect: (id: string) => void;
  onSelect?: (id: string) => void;
  selected?: boolean;
}> = ({ fp, onDisconnect, onSelect, selected }) => {
  const { t } = useTranslation('fanpage');
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${selected ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-100'}`}>
      <div className="flex items-center gap-4 p-4">
        {/* Avatar */}
        <img
          src={
            fp.picture_url ||
            `https://api.dicebear.com/7.x/shapes/svg?seed=${fp.page_id}&backgroundColor=1877F2`
          }
          alt={fp.page_name}
          className="w-14 h-14 rounded-xl object-cover bg-gray-100 flex-shrink-0"
        />

        {/* Info */}
        <button type="button" className="flex-1 min-w-0 text-left" onClick={() => onSelect?.(fp.id)}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900 text-[14px] truncate">{fp.page_name}</span>
            {fp.is_active ? (
              <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                Đang hoạt động
              </span>
            ) : (
              <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                Không hoạt động
              </span>
            )}
            {fp.webhook_verified && (
              <span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                {t('webhookVerified')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[12px] text-gray-400 flex-wrap">
            {fp.page_category && <span>{fp.page_category}</span>}
            {fp.fan_count > 0 && (
              <span className="flex items-center gap-1">
                <Users size={11} />
                {formatFanCount(fp.fan_count)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={11} />
              {formatDate(fp.last_sync_at || fp.created_at)}
            </span>
          </div>
        </button>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
            title={t('activityLog')}
          >
            <Activity size={16} />
            {expanded ? <ChevronUp size={12} className="inline ml-0.5" /> : <ChevronDown size={12} className="inline ml-0.5" />}
          </button>
          <button
            type="button"
            onClick={() => onDisconnect(fp.id)}
            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
            title={t('disconnect')}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* Activity log */}
      {expanded && (
        <div className="border-t border-gray-50 px-4 pb-4">
          <ActivityLog fanpageId={fp.id} />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export const FanpageManagement: React.FC = () => {
  const { t } = useTranslation('fanpage');
  const [tab, setTab] = useState<'connections' | 'content'>('connections');
  const [fanpages, setFanpages] = useState<FanpagePlatform[]>([]);
  const [selectedFanpageId, setSelectedFanpageId] = useState('');
  const [posts, setPosts] = useState<FanpagePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [error, setError] = useState('');
  const [needReconnect, setNeedReconnect] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState('');
  const [comments, setComments] = useState<FanpageComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const fetchFanpages = useCallback(async () => {
    setLoading(true);
    setError('');
      setNeedReconnect(false);
    try {
      const resp = await apiService.get<{ fanpages: FanpagePlatform[] }>('/api/v1/fanpages');
      const items = resp.fanpages ?? [];
      setFanpages(items);
      if (!selectedFanpageId && items[0]) {
        setSelectedFanpageId(items[0].id);
      }
      if (selectedFanpageId && !items.some((fanpage) => fanpage.id === selectedFanpageId)) {
        setSelectedFanpageId(items[0]?.id || '');
      }
    } catch (err: any) {
      setError(err?.message || 'Không thể tải danh sách Fanpage.');
    } finally {
      setLoading(false);
    }
  }, [selectedFanpageId]);

  const loadPosts = useCallback(async (refresh = false) => {
    if (!selectedFanpageId) return;
    const response = await apiService.get<{ posts: FanpagePost[] }>(`/api/v1/fanpages/${selectedFanpageId}/posts?limit=20`);
    setPosts(response.posts ?? []);
    setSelectedPostId('');
    setComments([]);
  }, [selectedFanpageId]);

  const loadComments = useCallback(async (postId: string) => {
    if (!selectedFanpageId || !postId) return;
    if (selectedPostId === postId) {
      // Toggle: clicking the same post again collapses the comment panel.
      setSelectedPostId('');
      setComments([]);
      return;
    }
    setSelectedPostId(postId);
    setComments([]);
    setLoadingComments(true);
    try {
      const resp = await apiService.get<{ comments: FanpageComment[] }>(
        `/api/v1/fanpages/${selectedFanpageId}/posts/${postId}/comments?limit=100`
      );
      setComments(resp.comments ?? []);
    } catch {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [selectedFanpageId, selectedPostId]);

  const refreshContent = useCallback(async () => {
    if (!selectedFanpageId) return;
    setSyncing(true);
    setNeedReconnect(false);
    setError('');
    try {
      await apiService.post(`/api/v1/fanpages/${selectedFanpageId}/sync?limit=20&comment_limit=0`, {});
      await loadPosts(true);
    } catch (err: any) {
      if (err?.needReconnect) {
        setNeedReconnect(true);
      }
      setError(err?.message || 'Không thể đồng bộ nội dung Fanpage.');
    } finally {
      setSyncing(false);
    }
  }, [selectedFanpageId, loadPosts]);

  useEffect(() => { fetchFanpages(); }, [fetchFanpages]);

  useEffect(() => {
    if (tab === 'content' && selectedFanpageId) {
      loadPosts();
    }
  }, [tab, selectedFanpageId, loadPosts]);

  useEffect(() => {
    const handleOAuthDone = (event: MessageEvent) => {
      // Accept only messages from this app origin (popup callback page).
      if (event.origin !== window.location.origin) return;

      const data = event.data as { type?: string; app?: string; connected?: boolean } | null;
      if (!data || data.type !== 'FANPAGE_OAUTH_DONE' || data.app !== 'marketing') return;

      if (data.connected) {
        fetchFanpages();
      }
    };

    window.addEventListener('message', handleOAuthDone);
    return () => window.removeEventListener('message', handleOAuthDone);
  }, [fetchFanpages]);

  const handleConnect = async () => {
    setNeedReconnect(false);
    setError('');
    setConnectLoading(true);
    try {
      const businessId = authService.getBusinessId() || '';
      const result = await apiService.post<{ nonce: string; oauth_url: string }>(
        '/api/v1/auth/fanpage/pre-auth',
        { business_id: businessId }
      );

      // Store state for CSRF check in callback page
      sessionStorage.setItem('fanpage_oauth_state', result.nonce);
      sessionStorage.setItem('fanpage_oauth_business_id', businessId);

      // Open FB OAuth in a popup and keep opener available so callback can
      // notify this window and close itself after completion.
      window.open(result.oauth_url, '_blank', 'width=600,height=700');
    } catch (err: any) {
      setError(err?.message || 'Không thể khởi tạo kết nối Facebook.');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async (fanpageId: string) => {
    if (!window.confirm(t('disconnectConfirm'))) return;
    try {
      await apiService.delete(`/api/v1/fanpages/${fanpageId}`);
      setFanpages((prev) => prev.filter((fp) => fp.id !== fanpageId));
    } catch (err: any) {
      setError(err?.message || 'Không thể huỷ kết nối Fanpage.');
    }
  };

  const selectedFanpage = fanpages.find((fp) => fp.id === selectedFanpageId) || null;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-tg-section-separator bg-tg-section-bg flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Facebook size={20} className="text-[#1877F2]" />
          <h3 className="text-lg font-semibold text-tg-text">Fanpage đã kết nối</h3>
          {!loading && (
            <span className="text-sm text-tg-hint ml-1">({fanpages.length})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchFanpages}
            disabled={loading}
            className="p-2 rounded-lg hover:bg-tg-section-separator text-tg-hint hover:text-tg-text transition-colors disabled:opacity-40"
            title={t('refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connectLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1877F2] text-white text-sm font-semibold hover:bg-[#166fe5] transition-colors disabled:opacity-50"
          >
            <Plus size={16} />
            {connectLoading ? t('loading') : t('connectFanpage')}
          </button>
        </div>
      </div>

      <div className="px-4 pt-3 flex gap-2 border-b border-gray-100 bg-white">
        <button type="button" onClick={() => setTab('connections')} className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === 'connections' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}>
          Kết nối & nhật ký
        </button>
        <button type="button" onClick={() => setTab('content')} className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === 'content' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}>
          Bài viết Fanpage
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-[13px] text-red-700">
          <XCircle size={14} className="flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {needReconnect && (
            <button
              type="button"
              onClick={handleConnect}
              className="ml-2 px-2 py-1 bg-red-600 text-white text-[11px] font-semibold rounded-lg whitespace-nowrap hover:bg-red-700 transition-colors"
            >
              Kết nối lại →
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw size={24} className="animate-spin text-gray-300" />
          </div>
        ) : fanpages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <Facebook size={48} className="text-gray-200 mb-3" />
            <p className="text-tg-text font-semibold mb-1">{t('noFanpages')}</p>
            <p className="text-tg-hint text-sm">
              {t('connectWithFacebook')}
            </p>
          </div>
        ) : tab === 'connections' ? (
          <div className="space-y-3">
            {fanpages.map((fp) => (
              <FanpageCard key={fp.id} fp={fp} onDisconnect={handleDisconnect} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4 h-full">
            <div className="col-span-12 xl:col-span-3 space-y-3">
              {fanpages.map((fp) => (
                <FanpageCard
                  key={fp.id}
                  fp={fp}
                  onDisconnect={handleDisconnect}
                  onSelect={setSelectedFanpageId}
                  selected={fp.id === selectedFanpageId}
                />
              ))}
            </div>

            <div className="col-span-12 xl:col-span-9 space-y-4">
              <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm text-gray-400">Fanpage đang chọn</div>
                    <div className="text-lg font-semibold text-gray-900">{selectedFanpage?.page_name || 'Chưa chọn Fanpage'}</div>
                    <div className="mt-1 text-xs text-gray-500">Bài viết được lấy trực tiếp từ Facebook (không lưu vào DB). Nhấn vào bài viết để xem bình luận chi tiết.</div>
                  </div>
                  <button
                    type="button"
                    onClick={refreshContent}
                    disabled={syncing || !selectedFanpageId}
                    className="px-3 py-2 rounded-xl bg-blue-50 text-blue-600 font-medium disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                    {syncing ? 'Đang sync...' : 'Sync nội dung'}
                  </button>
                </div>
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Page ID</div>
                    <div className="mt-1 font-medium text-gray-900 break-all">{selectedFanpage?.page_id || '—'}</div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Last Sync</div>
                    <div className="mt-1 font-medium text-gray-900">{formatDate(selectedFanpage?.last_sync_at || '')}</div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Posts Loaded</div>
                    <div className="mt-1 font-medium text-gray-900">{posts.length}</div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Data Scope</div>
                    <div className="mt-1 font-medium text-gray-900">Post ID, nội dung, thời gian, permalink</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">Danh sách bài viết</h4>
                    <div className="text-[11px] text-gray-400">Sắp xếp theo created_time giảm dần từ dữ liệu live của Facebook. Post ID ở đây là key để truyền sang workflow nodes.</div>
                  </div>
                  <span className="text-xs text-gray-400">{posts.length} bài</span>
                </div>
                <div className="space-y-3 max-h-[720px] overflow-y-auto pr-1">
                  {posts.map((post, index) => (
                    <div key={post.post_id} className={`rounded-2xl border bg-white p-4 transition-colors ${selectedPostId === post.post_id ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'}`}>
                      <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => loadComments(post.post_id)}>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Post #{index + 1}</div>
                          <div className="mt-1 text-[12px] text-gray-500">{formatDate(post.created_time || post.created_at || '')}</div>
                        </div>
                        {post.permalink_url && (
                          <a href={post.permalink_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
                            <ExternalLink size={12} />Mở bài viết
                          </a>
                        )}
                      </div>
                      <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-gray-400">Post ID</div>
                        <div className="mt-1 break-all font-mono text-[12px] text-gray-800">{post.post_id}</div>
                      </div>
                      <div className="mt-3 text-sm leading-6 text-gray-900 whitespace-pre-wrap">{post.message?.trim() || '(Bài viết không có text body)'}</div>
                      <div className="mt-4 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">👍 Likes: {formatMetric(post.like_count)}</span>
                        <button
                          type="button"
                          onClick={() => loadComments(post.post_id)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${selectedPostId === post.post_id ? 'bg-blue-100 text-blue-700 font-semibold' : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                        >
                          <MessageSquare size={11} />
                          {formatMetric(post.comment_count)} bình luận
                        </button>
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">↗ Shares: {formatMetric(post.share_count)}</span>
                      </div>

                      <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Người tương tác/comment</div>
                        {post.participants && post.participants.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {post.participants.map((participant, idx) => {
                              const name = participant.participant_name || 'Người dùng ẩn danh';
                              const count = participant.comment_count ?? 0;
                              const key = `${participant.participant_id || name}-${idx}`;
                              const content = (
                                <>
                                  <span className="truncate max-w-[180px]">{name}</span>
                                  {count > 0 && <span className="text-gray-400">({count})</span>}
                                </>
                              );

                              if (participant.profile_url) {
                                return (
                                  <a
                                    key={key}
                                    href={participant.profile_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-2.5 py-1 text-[12px] text-blue-700 hover:bg-blue-50"
                                  >
                                    {content}
                                  </a>
                                );
                              }

                              return (
                                <span
                                  key={key}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[12px] text-gray-700"
                                >
                                  {content}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[12px] text-gray-400">Chưa có dữ liệu người tương tác/comment cho bài viết này.</p>
                        )}
                      </div>

                      {/* Inline comment panel */}
                      {selectedPostId === post.post_id && (
                        <div className="mt-4 border-t border-gray-100 pt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare size={13} className="text-blue-500" />
                            <span className="text-[12px] font-semibold text-gray-700">Bình luận trực tiếp từ Facebook</span>
                          </div>
                          {loadingComments ? (
                            <div className="flex items-center gap-2 py-4 text-[12px] text-gray-400">
                              <RefreshCw size={13} className="animate-spin" />
                            <span>Đang tải bình luận...</span>
                            </div>
                          ) : comments.length === 0 ? (
                            <p className="py-4 text-center text-[12px] text-gray-400">{t('noCommentsYet')}</p>
                          ) : (
                            <div className="space-y-2.5 max-h-60 overflow-y-auto">
                              {comments.map((cmt) => (
                                <div key={cmt.comment_id} className="rounded-xl bg-gray-50 px-3 py-2.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {cmt.profile_url ? (
                                        <a href={cmt.profile_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[12px] font-semibold text-blue-600 hover:underline truncate">
                                          {cmt.from_name || 'Người dùng ẩn danh'}
                                        </a>
                                      ) : (
                                        <span className="text-[12px] font-semibold text-gray-700 truncate">{cmt.from_name || 'Người dùng ẩn danh'}</span>
                                      )}
                                      {cmt.is_hidden && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 rounded-full">Ẩn</span>}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0 text-[11px] text-gray-400">
                                      {(cmt.like_count ?? 0) > 0 && <span>👍 {cmt.like_count}</span>}
                                      {(cmt.reply_count ?? 0) > 0 && <span>↩ {cmt.reply_count}</span>}
                                      <span>{formatDate(cmt.created_time || '')}</span>
                                    </div>
                                  </div>
                                  <p className="mt-1 text-[13px] text-gray-800 whitespace-pre-wrap">{cmt.message || '(không có nội dung)'}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {posts.length === 0 && (
                    <div className="py-10 text-center text-sm text-gray-500">Chưa có bài viết nào từ Facebook hiện tại. Bấm Sync nội dung để kiểm tra kết nối và tải lại.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FanpageManagement;
